/**
 * Mutations for DigitalEvent (VideoChat module)
 */
import Vue from "vue";
import { VideoMember } from "@/components/DigitalEvent/models/VideoMember";

export default {
  /**
   * Save mediaAccessError to the store
   * @param state {Object}
   * @param error {String|null}
   */
  setMediaAccessError(state, error) {
    state.mediaAccessError = error;
  },

  /**
   * Set video call initiator
   * @param state {Object}
   * @param value {String|null}
   */
  setVideoCallInitiator(state, value) {
    state.videoCallInitiator = value;
  },

  /**
   * Set list of video chat members
   * @param state {Object}
   * @param value {Array}
   */
  setVideoChatMembers(state, value) {
    state.videoChatMembers = value;
  },

  /**
   * Save user's video chat list to the store
   * @param state {Object}
   * @param videoChats {Array}
   */
  setVideoChats(state, videoChats) {
    state.videoChats = videoChats;
  },

  /**
   * Add video chat to the list in the store
   * @param state {Object}
   * @param videoChat {Object}
   */
  addVideoChat(state, videoChat) {
    state.videoChats.push(videoChat);
  },

  /**
   * Update video chat in the store
   * @param state {Object}
   * @param videoChatUUID {String}
   * @param data {Object}
   */
  updateVideoChat(state, { videoChatUUID, data }) {
    let videoChat = state.videoChats.find(chat => chat.groupUUID === videoChatUUID);
    if (videoChat) {
      Object.assign(videoChat, data);

      if (state.activeVideoChat) {
        state.activeVideoChat = videoChat;
      }
    }
  },

  /**
   * Delete video chat from the store
   * @param state {Object}
   * @param videoChatUUID {String}
   */
  deleteVideoChat(state, videoChatUUID) {
    let videoChatIndex = state.videoChats.findIndex(videoChat => videoChat.groupUUID === videoChatUUID);
    if (~videoChatIndex) {
      state.videoChats.splice(videoChatIndex, 1);
    }
  },

  /**
   * Save active video chat data to the store
   * @param state {Object}
   * @param videoChat {Object}
   */
  setActiveVideoChat(state, videoChat) {
    if (videoChat) {
      if (!state.activeVideoChat) {
        Object.assign(videoChat, { startedAt: new Date() });
        state.activeVideoChat = videoChat;
      }
    } else {
      state.activeVideoChat = null;
    }
  },

  /**
   * Set active video session
   * @param state {Object}
   * @param session {Object}
   */
  setActiveVideoSession(state, session) {
    state.activeVideoSession = session;
  },

  /**
   * Set videoChat member
   * @param state {Object}
   * @param userUUID {String} user.uuid
   * @param data {Object} Dict with user's info
   */
  setVideoChatMember(state, { userUUID, data }) {
    let videoChatMemberIndex = state.videoChatMembers.findIndex(member => member.userUUID === userUUID);
    if (~videoChatMemberIndex) {
      Vue.set(
        state.videoChatMembers,
        videoChatMemberIndex,
        Object.assign(state.videoChatMembers[videoChatMemberIndex], data)
      );
    } else {
      state.videoChatMembers.push(new VideoMember(userUUID, data));
    }
  },

  /**
   * Delete videoChat Member
   * @param state {Object}
   * @param userUUID {String} user.uuid
   */
  deleteVideoChatMember(state, userUUID) {
    let videoChatMemberIndex = state.videoChatMembers.findIndex(member => member.userUUID === userUUID);
    if (~videoChatMemberIndex) {
      state.videoChatMembers.splice(videoChatMemberIndex, 1);
    }
  },

  /**
   * Add stream to the store
   * @param state {Object}
   * @param stream {Object}
   */
  addStream(state, stream) {
    state.streams.push(stream);
  },

  /**
   * Delete stream from the store
   * @param state {Object}
   * @param stream {Object}
   */
  deleteStream(state, stream) {
    const idx = state.streams.indexOf(stream);
    if (idx > -1) {
      state.streams.splice(idx, 1);
    }
  },

  /**
   * Delete all streams from the state
   * @param state {Object}
   */
  deleteStreams(state) {
    Vue.set(state, "streams", []);
  },

  /**
   * Add subscriber to the state
   * @param state {Object}
   * @param subscriber {Object}
   */
  addSubscriber(state, subscriber) {
    state.subscribers.push(subscriber);
  },

  /**
   * Delete all subscribers from the state
   * @param state {Object}
   */
  deleteSubscribers(state) {
    Vue.set(state, "subscribers", []);
  },

  /**
   * Set connections count
   * @param state {Object}
   * @param value {Integer}
   */
  setConnectionsCount(state, value) {
    state.connectionsCount = value || 0;
  },

  /**
   * Increment connections count
   * @param state {Object}
   */
  incrementConnectionsCount(state) {
    state.connectionsCount = state.connectionsCount + 1;
  },

  /**
   * Decrement connections count
   * @param state {Object}
   */
  decrementConnectionsCount(state) {
    state.connectionsCount = state.connectionsCount - 1;
  },

  /**
   * Delete all subscribers/invitees from videoChatMembers array
   * @param state {Object}
   */
  deleteVideoChatMembers(state) {
    state.videoChatMembers.splice(1, state.videoChatMembers.length - 1);
  },

  setParticipantFullName(state, value) {
    state.oneToOneChatParticipantFullName = value;
  },
  setActiveVideoDevice(state, deviceId) {
    state.activeVideoDevice = deviceId;
  }
};
